/**
 * Ficha do inadimplente — o que abre ao clicar no cliente da lista.
 *
 * POR QUE existe: antes, clicar no nome saltava direto para a ficha do
 * contato, que fala de venda e não de cobrança — e lá "Parcela" é o VALOR da
 * mensalidade, nunca quantas estão em aberto. Quem cobra precisa da conta de
 * cobrança antes de sair da tela. O botão para o contato continua aqui, então
 * nada se perdeu.
 */

import { AlertTriangle, DollarSign, ExternalLink, Phone, User } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { formatBRL as formatCurrency } from '@/lib/format';

import { calcularParcelasEmAberto, rotuloParcelasEmAberto, type RegistroInadimplente } from './parcelasEmAberto';
import { rotaPermitidaLeitura, useSomenteLeitura } from '../commons/somenteLeitura';
import { useMascara } from '../valores-ocultos/contexto';
import { CotaLink } from '../comissoes/CotaLink';

export interface InadimplenteSelecionado extends RegistroInadimplente {
  id: number;
  cota: string;
  contrato: string;
  consorciado: string;
  celular?: string | null;
  vendedor_nome?: string | null;
}

interface Props {
  inadimplente: InadimplenteSelecionado | null;
  onFechar: () => void;
  onAbrirContato: (consorciado: string) => void;
}

const Linha = ({ rotulo, valor }: { rotulo: string; valor: React.ReactNode }) => (
  <div className="flex items-baseline justify-between gap-4 border-b border-border/60 py-2 last:border-b-0">
    <span className="text-sm text-muted-foreground">{rotulo}</span>
    <span className="text-sm font-medium tabular-nums text-right">{valor}</span>
  </div>
);

export const DetalheInadimplente = ({ inadimplente, onFechar, onAbrirContato }: Props) => {
  const { perfil } = useSomenteLeitura();
  const m = useMascara();
  const podeAbrirContato = !perfil || rotaPermitidaLeitura(perfil, '/contacts');
  const parcelas = calcularParcelasEmAberto(inadimplente);

  return (
    <Dialog open={inadimplente !== null} onOpenChange={(aberto) => { if (!aberto) onFechar(); }}>
      <DialogContent className="sm:max-w-md">
        {inadimplente && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                {inadimplente.consorciado}
              </DialogTitle>
              <DialogDescription>
                Contrato {inadimplente.contrato} • Cota {inadimplente.cota ? <CotaLink cota={inadimplente.cota} contrato={inadimplente.contrato} /> : '—'}
              </DialogDescription>
            </DialogHeader>

            {/* O número que o cliente pediu, em destaque e com o rótulo dizendo
                o que ele conta — não é dias, não é reais, são parcelas. */}
            <div className="rounded-[14px] bg-primary px-5 py-4 text-primary-foreground">
              <p className="text-xs uppercase tracking-wide opacity-80">Parcelas em aberto</p>
              <p className="mt-1 text-4xl font-bold tabular-nums">
                {m(parcelas.emAberto ?? '—')}
              </p>
              <p className="mt-1 text-xs opacity-90">{m(rotuloParcelasEmAberto(parcelas))}</p>
            </div>

            <div className="mt-1">
              <Linha
                rotulo="Parcelas já pagas"
                valor={parcelas.pagas == null ? 'Servopa não informou' : m(parcelas.pagas)}
              />
              <Linha
                rotulo="Valor total em atraso"
                valor={
                  inadimplente.valor_atraso === null || inadimplente.valor_atraso === undefined
                    ? '—'
                    : m(formatCurrency(Number(inadimplente.valor_atraso)))
                }
              />
              <Linha
                rotulo="Média por parcela em aberto"
                valor={
                  parcelas.valorMedioPorParcela === null
                    ? '—'
                    : m(formatCurrency(parcelas.valorMedioPorParcela))
                }
              />
              {inadimplente.vendedor_nome && (
                <Linha rotulo="Vendedor" valor={inadimplente.vendedor_nome} />
              )}
              {inadimplente.celular && (
                <Linha
                  rotulo="Celular"
                  valor={
                    <span className="inline-flex items-center gap-1">
                      <Phone className="h-3 w-3 text-muted-foreground" />
                      {inadimplente.celular}
                    </span>
                  }
                />
              )}
            </div>

            {/* Aviso honesto: sem prazo do plano na base, "faltam X para
                quitar o consórcio" seria chute numa tela de cobrança. */}
            <Badge variant="outline" className="w-full justify-start gap-2 whitespace-normal py-2 text-left font-normal">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">
                Parcelas restantes não exibidas: a Servopa não informa o prazo total do plano.
              </span>
            </Badge>

            <DialogFooter className="gap-2 sm:justify-between">
              <span className="hidden items-center gap-1 text-xs text-muted-foreground sm:flex">
                <DollarSign className="h-3 w-3" />
                Valores da última sincronização com a Servopa
              </span>
              {podeAbrirContato && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onAbrirContato(inadimplente.consorciado)}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Abrir contato
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
